/**
 * Per-step wizard schemas, lifted from the mobile screens so web and
 * mobile share a single source of truth for form validation.
 *
 * Phase 18 (Refactor 2) lifted these out of
 * `apps/mobile/screens/wizards/Wizard*.tsx`. The web wizards under
 * `components/documents/*-wizard.tsx` historically used ad-hoc
 * `trim()`/`length` checks; they can adopt these schemas in a later
 * refactor without further changes here.
 *
 * `WIZARD_SCHEMAS` exposes every wizard keyed by the same string the
 * mobile draft hook (`useWizardDraft`) uses for `documentType`:
 *
 *   - `RAMS`
 *   - `CPP`
 *   - `CPP_LIGHTWEIGHT`     (variant of CPP — see CLAUDE.md memory)
 *   - `SITE_INDUCTION`
 *   - `EMERGENCY_PLAN`
 *   - `TOOLBOX_TALK`
 *
 * Each entry has `steps` (in display order, including the no-op preview
 * step) and `full` (composite schema for end-of-wizard validation).
 */
import type { ZodType } from "zod";

import {
  RAMS_WIZARD_STEPS,
  RamsWizardFullSchema,
} from "./rams";
import {
  CPP_WIZARD_STEPS,
  CppWizardFullSchema,
} from "./cpp";
import {
  LIGHTWEIGHT_CPP_WIZARD_STEPS,
  LightweightCppWizardFullSchema,
} from "./lightweight-cpp";
import {
  SITE_INDUCTION_WIZARD_STEPS,
  SiteInductionWizardFullSchema,
} from "./site-induction";
import {
  EMERGENCY_PLAN_WIZARD_STEPS,
  EmergencyPlanWizardFullSchema,
} from "./emergency-plan";
import {
  TOOLBOX_TALK_WIZARD_STEPS,
  ToolboxTalkWizardFullSchema,
} from "./toolbox-talk";

export * from "./nearest-hospital";
export * from "./rams";
export * from "./cpp";
export * from "./lightweight-cpp";
export * from "./site-induction";
export * from "./emergency-plan";
export * from "./toolbox-talk";

/**
 * Wire-format key for `WIZARD_SCHEMAS`. Matches the `documentType`
 * string passed to `useWizardDraft` on mobile — `CPP_LIGHTWEIGHT` is
 * the wizard-side variant tag even though it persists as
 * `DocumentType.CPP` in Prisma.
 */
export const WIZARD_KINDS = [
  "RAMS",
  "CPP",
  "CPP_LIGHTWEIGHT",
  "SITE_INDUCTION",
  "EMERGENCY_PLAN",
  "TOOLBOX_TALK",
] as const;
export type WizardKind = (typeof WIZARD_KINDS)[number];

export interface WizardStepDescriptor {
  readonly id: string;
  readonly schema: ZodType;
}

export interface WizardSchemaBundle {
  readonly steps: readonly WizardStepDescriptor[];
  readonly full: ZodType;
}

export const WIZARD_SCHEMAS: Readonly<Record<WizardKind, WizardSchemaBundle>> =
  {
    RAMS: {
      steps: RAMS_WIZARD_STEPS,
      full: RamsWizardFullSchema,
    },
    CPP: {
      steps: CPP_WIZARD_STEPS,
      full: CppWizardFullSchema,
    },
    CPP_LIGHTWEIGHT: {
      steps: LIGHTWEIGHT_CPP_WIZARD_STEPS,
      full: LightweightCppWizardFullSchema,
    },
    SITE_INDUCTION: {
      steps: SITE_INDUCTION_WIZARD_STEPS,
      full: SiteInductionWizardFullSchema,
    },
    EMERGENCY_PLAN: {
      steps: EMERGENCY_PLAN_WIZARD_STEPS,
      full: EmergencyPlanWizardFullSchema,
    },
    TOOLBOX_TALK: {
      steps: TOOLBOX_TALK_WIZARD_STEPS,
      full: ToolboxTalkWizardFullSchema,
    },
  };
